/**
 * Turning a walkthrough's authored route template into a path this department can navigate to.
 *
 * Pure, and separate from the controller so the "never navigate to a half-filled template" rule has one
 * home and can be tested directly.
 */

/**
 * Every `:name` in a route template, matched as a WHOLE placeholder.
 *
 * The boundary is the point. Substituting `:date` by plain string replacement also rewrites the head of
 * `:dateString` — the two names a tour can author today — leaving `2026-09-14String`: a path with no
 * colon left in it, so the guard below waves it through and the user lands on a 404 instead of being
 * refused out loud.
 */
const PLACEHOLDER = /:(\w+)/g;

/** The weekly Staffing grid as tours author it. Landing there takes more than a navigate — see `useWalkthroughLaunch`. */
export const STAFFING_WEEK_ROUTE = "/:departmentId/staffing/:dateString";

/**
 * Fill a route template from the resolved params. `:departmentId` comes from the client, not the asset.
 *
 * Null when a placeholder is left unfilled — a path with a literal `:param` matches no route and lands
 * the user nowhere, silently. Returning null rather than navigating anyway is what lets the caller
 * refuse out loud instead of stranding them on a blank page.
 */
export const buildPath = (route: string, params: Record<string, string>, departmentId: string): string | null => {
	// A Map rather than an object literal: params arrive off the wire, and a param named `constructor` or
	// `toString` would otherwise resolve against Object.prototype and substitute a function body.
	const values = new Map<string, string>([...Object.entries(params), ["departmentId", departmentId]]);
	const path = route.replaceAll(PLACEHOLDER, (placeholder, name: string) => {
		const value = values.get(name);
		// Empty is unfilled, not filled-with-nothing: `/dept/staffing/` matches no route either, and leaving
		// the placeholder in turns it into the refusal the caller already knows how to say out loud.
		return value === undefined || value === "" ? placeholder : encodeURIComponent(value);
	});
	return path.includes(":") ? null : path;
};

/** The param values in `pathname` if it is this route for this department, else null. */
const paramsHere = (route: string, departmentId: string, pathname: string): Record<string, string> | null => {
	const names: string[] = [];
	const pattern = route.replace(/:(\w+)/g, (_match, name: string) => {
		names.push(name);
		return "([^/]+)";
	});
	const found = new RegExp(`^${pattern}$`).exec(pathname);
	if (found === null) return null;
	const values: Record<string, string> = {};
	for (const [index, name] of names.entries()) {
		// A lone `%` throws, and both callers run synchronously in the launch effect — outside the async
		// scan's try/catch — so it would surface as an uncaught React error instead of a refusal.
		try {
			values[name] = decodeURIComponent(found[index + 1] ?? "");
		} catch {
			return null;
		}
	}
	return values.departmentId !== undefined && values.departmentId !== departmentId ? null : values;
};

/** Whether the user is on this route at all, whatever state it is showing. */
export const isOnRoute = (route: string, departmentId: string, pathname: string): boolean => paramsHere(route, departmentId, pathname) !== null;

/**
 * Whether the page the user is on can run the tour as it stands.
 *
 * A calendar-resolved param (`today`, `week_start`) rejects only values EARLIER than the one the tour
 * resolved. A later day or week is fine — the controls behave identically — while an earlier one is not: a
 * past day's Post Shift tab renders `disabled`, so the tour would point at something dead. Launch params
 * are ISO dates, which compare correctly as plain strings.
 *
 * A param named in `exactParams` was picked from department DATA (the day that has a shift inside the
 * call-off window), so it must match exactly: the day after it may have nothing the tour can open.
 */
export const isRunnableHere = (
	route: string,
	params: Record<string, string>,
	exactParams: readonly string[],
	departmentId: string,
	pathname: string,
): boolean => {
	const values = paramsHere(route, departmentId, pathname);
	if (values === null) return false;
	return Object.entries(params).every(([name, resolved]) => {
		const here = values[name];
		// A param the route does not carry is not runnable. Fail-closed on purpose; unreachable while params
		// name their own route's segments.
		if (here === undefined) return false;
		return exactParams.includes(name) ? here === resolved : here >= resolved;
	});
};
